// src/services/vendorChat/chat.ts
import { useQuery, useQueryClient } from 'react-query';
import { AxiosInstance } from '../axios.config';
import { api } from '../constants';
import { vendorChatWebSocket } from './websocket';
import { useEffect, useRef } from 'react';
import TokenService from '../service.token';
import { IChat, IChatListResponse, IMessage } from './types';

export const getChats = (page = 1, page_size = 20): Promise<IChatListResponse> =>
  AxiosInstance.get(api.message.list, { params: { page, page_size } }).then((r) => r.data);

export const useGetChatsQuery = (page = 1, page_size = 20) => {
  const queryClient = useQueryClient();
  const isConnectedRef = useRef(false);

  const query = useQuery<IChatListResponse, Error>(
    ['vendor-chats', page, page_size],
    () => getChats(page, page_size),
    {
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    const token = TokenService.getToken()?.access;
    if (!token) {
      console.warn('No token available, skipping WebSocket connection');
      return;
    }

    // Prevent multiple connections in Strict Mode
    if (isConnectedRef.current) {
      return;
    }

    const handleUpdate = (updatedChats: IChat[]) => {
      queryClient.setQueryData<IChatListResponse>(['vendor-chats', page, page_size], (old) => {
        if (!old) {
          return {
            count: updatedChats.length,
            next: null,
            previous: null,
            results: updatedChats,
          };
        }
        return { ...old, results: [...updatedChats] };
      });
    };

    // Small delay to allow React Strict Mode to settle
    const connectTimer = setTimeout(() => {
      isConnectedRef.current = true;
      vendorChatWebSocket.connectGlobal(handleUpdate, queryClient);
    }, 100);

    return () => {
      clearTimeout(connectTimer);
      // Only disconnect if we actually connected
      if (isConnectedRef.current) {
        isConnectedRef.current = false;
        vendorChatWebSocket.disconnectGlobal();
      }
    };
  }, [queryClient, page, page_size]);

  return query;
};

export const getChatById = (chatId: number): Promise<IChat> =>
  AxiosInstance.get(`${api.message.detail}/${chatId}/`).then((r) => r.data);

export const useGetChatQuery = (chatId: number | null) => {
  const queryClient = useQueryClient();
  const isConnectedRef = useRef(false);

  const query = useQuery<IChat, Error>(['vendor-chat', chatId], () => getChatById(chatId!), {
    enabled: !!chatId,
    staleTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!chatId) return;

    const token = TokenService.getToken()?.access;
    if (!token) {
      console.warn('No token available, skipping chat WebSocket connection');
      return;
    }

    // Prevent multiple connections in Strict Mode
    if (isConnectedRef.current) {
      return;
    }

    const handleMessage = (msg: IMessage) => {
      if (!msg.content?.trim() || !msg.sender?.id) return;
      queryClient.setQueryData<IChat | undefined>(['vendor-chat', chatId], (old) => {
        if (!old) return old;
        const exists = old.messages.some((m) => m.id === msg.id);
        if (exists) return old;
        return { ...old, messages: [...old.messages, msg], updated_at: msg.updated_at };
      });
    };

    // Small delay to allow React Strict Mode to settle
    const connectTimer = setTimeout(() => {
      isConnectedRef.current = true;
      vendorChatWebSocket.connectChat(chatId, handleMessage, queryClient);
    }, 100);

    return () => {
      clearTimeout(connectTimer);
      // Only disconnect if we actually connected
      if (isConnectedRef.current) {
        isConnectedRef.current = false;
        vendorChatWebSocket.disconnectChat(chatId);
      }
    };
  }, [chatId, queryClient]);

  return query;
};
